import React from "react";
import { FaCar } from "react-icons/fa";
import { QRCodeSVG } from "qrcode.react";
import { BASE_URL } from "../../config";
import talentLogo2 from "../../assets/talentlogo2.png";
import { groupTimesheetsIntoSlots } from "../../utils/timesheetSlots";
import {
  shiftRowHasTravel,
  travelPayRateSuffix,
} from "../../utils/shiftTravelDisplay";
import TimesheetWeekView from "./TimesheetWeekView";
import {
  buildWeekStaffRows,
  filterTimesheetsToWeekRange,
  groupTimesheetsByLocation,
  resolveStaffRequestIdsFromRows,
  resolveSupervisorsFromRows,
  timesheetRowDateKey,
} from "../../utils/timesheetWeekRows";
import {
  addAppCalendarDays,
  formatAppDate,
  formatAppIntlLabel,
  getAppTodayIso,
  getAppWallDateParts,
  parseDbDateTime,
} from "../../utils/appDateTime";
import { resolveBackendUrl } from "../../utils/accountAvatarUrl";
import {
  AvatarSmall,
  PlaceholderAvatar,
  formatTime,
  formatTimeRange,
  formatDuration,
  getAvatarColorClass,
} from "../../utils/timesheetViewHelpers";

export default function TimesheetsPrintableContent({
  data = [],
  loading = false,
  filterClient,
  clientSearch,
  filterClientList = [],
  filterLocations = [],
  filterLocationList = [],
  filterMode,
  filterStartDate,
  filterRange,
  searchTerm = "",
  timesheetPrintOrientation = "landscape",
  printWithoutValues = false,
  selectedRequest = null,
  editableRequest = null,
  supervisors = [],
}) {
  if (!filterClient) {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-lg border border-border p-6">
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <p className="text-muted-foreground text-lg mb-2">
              Select a client to view timesheets.
            </p>
            <p className="text-sm text-muted-foreground">
              Open the filter panel and choose a client to see timesheet data, total hours, and other details.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full min-w-0 max-w-full space-y-6 pb-2">
      <div className="w-full min-w-0 max-w-full overflow-x-auto timesheet-mobile-scroll timesheet-print-wrap">
        {/* Timesheet Card */}
        <div
          id="printable-timesheet"
          className={`bg-white rounded-lg border border-border p-6 min-w-[768px] md:min-w-0 print-timesheet-root ${
            filterMode === "week" || timesheetPrintOrientation === "landscape"
              ? "print-landscape-layout"
              : ""
          } ${filterMode === "week" ? "print-week-layout" : "print-day-mode"}`}
        >
              {/* Schedule Tables - One per day if range is selected */}
              {(() => {
                const selectedLocationIds = new Set(
                  (filterLocations || []).map((id) => String(id).trim()).filter(Boolean),
                );
                const getRowLocationId = (row) =>
                  String(
                    row?.client_location_id ||
                      row?.clientLocationId ||
                      row?.location_id ||
                      row?.locationId ||
                      row?.setting_client_location_id ||
                      "",
                  ).trim();
                const displayRows =
                  selectedLocationIds.size > 0
                    ? (data || []).filter((row) =>
                        selectedLocationIds.has(getRowLocationId(row)),
                      )
                    : data || [];

                // Calculate date range based on start date + mode/range
                const dateRange = [];
                if (filterStartDate) {
                  let rangeDays = 0;
                  if (filterRange && parseInt(filterRange, 10) > 0) {
                    rangeDays = parseInt(filterRange, 10);
                  } else if (filterMode === "week") {
                    rangeDays = 7;
                  } else if (filterMode === "day") {
                    rangeDays = 7;
                  }
                  if (rangeDays > 0) {
                    for (let i = 0; i < rangeDays; i++) {
                      dateRange.push(addAppCalendarDays(filterStartDate, i));
                    }
                  } else {
                    dateRange.push(filterStartDate);
                  }
                }

                // Group timesheets by date
                const timesheetsByDate = new Map();
                displayRows.forEach((timesheet) => {
                  const dateKey = timesheetRowDateKey(
                    timesheet,
                    filterStartDate || "",
                  );

                  if (!timesheetsByDate.has(dateKey)) {
                    timesheetsByDate.set(dateKey, []);
                  }
                  timesheetsByDate.get(dateKey).push(timesheet);
                });

                // Show only dates that have at least one timesheet row.
                const datesWithData = dateRange.filter((dateStr) => {
                  const dateTimesheets = timesheetsByDate.get(dateStr) || [];
                  return dateTimesheets.length > 0;
                });

                const finalDateRange =
                  datesWithData.length > 0
                    ? datesWithData
                    : dateRange.length === 0 && displayRows.length > 0
                      ? Array.from(timesheetsByDate.keys()).sort()
                      : [];

                /* Top print header (logo, client, QR) — only when there are rows to show for current filters */
                const baseDateHdr = filterStartDate || getAppTodayIso();
                const rangeDaysHdr = filterRange
                  ? parseInt(filterRange, 10)
                  : filterMode === "week"
                    ? 7
                    : 7;
                const endDateIsoHdr = addAppCalendarDays(baseDateHdr, Math.max(rangeDaysHdr, 1) - 1);
                const startAnchorHdr = parseDbDateTime(`${baseDateHdr}T12:00:00Z`);
                const endAnchorHdr = parseDbDateTime(`${endDateIsoHdr}T12:00:00Z`);
                let dateRangeLabel;
                if (
                  rangeDaysHdr <= 1 ||
                  baseDateHdr === endDateIsoHdr
                ) {
                  dateRangeLabel = formatAppDate(startAnchorHdr, { emptyValue: baseDateHdr });
                } else {
                  const startParts = getAppWallDateParts(startAnchorHdr);
                  const endParts = getAppWallDateParts(endAnchorHdr);
                  if (
                    startParts &&
                    endParts &&
                    startParts.month === endParts.month &&
                    startParts.year === endParts.year
                  ) {
                    dateRangeLabel = `${formatAppIntlLabel(startAnchorHdr, { month: "short", day: "numeric" })} \u2013 ${endParts.day}, ${endParts.year}`;
                  } else {
                    dateRangeLabel = `${formatAppIntlLabel(startAnchorHdr, { month: "short", day: "numeric" })} \u2013 ${formatAppIntlLabel(endAnchorHdr, { month: "short", day: "numeric", year: "numeric" })}`;
                  }
                }
                const supKeysHdr = [
                  "supervisor",
                  "shiftSupervisor",
                  "supervisors",
                  "supervisorName",
                ];
                const supervisorsListHdr = (() => {
                  try {
                    const vals = displayRows
                      .map((dd) => {
                        for (const k of supKeysHdr) if (dd?.[k]) return dd[k];
                        return null;
                      })
                      .filter(Boolean);
                    return Array.from(new Set(vals)).join(", ");
                  } catch (_) {
                    return "";
                  }
                })();
                const staffRequestIdsHdr = (() => {
                  try {
                    const ids = displayRows
                      .map((dd) => dd.staff_request_id || dd.staffRequestId)
                      .filter(Boolean);
                    return Array.from(new Set(ids)).join(", ");
                  } catch (_) {
                    return "";
                  }
                })();
                const positionSummaryHdr = (() => {
                  try {
                    const pos = displayRows
                      .map((dd) => dd.position_name || dd.position || dd.role)
                      .filter(Boolean);
                    return Array.from(new Set(pos)).slice(0, 3).join(", ");
                  } catch (_) {
                    return "";
                  }
                })();
                const derivedLocationLabelsHdr = (() => {
                  try {
                    const labels = displayRows
                      .map(
                        (dd) =>
                          dd?.client_location ||
                          dd?.clientLocationName ||
                          dd?.location_name ||
                          dd?.location ||
                          dd?.locationName ||
                          dd?.full_address ||
                          dd?.address ||
                          "",
                      )
                      .map((v) => String(v || "").trim())
                      .filter(Boolean);
                    return Array.from(new Set(labels));
                  } catch (_) {
                    return [];
                  }
                })();
                const locationLabelHdr =
                  derivedLocationLabelsHdr.length > 1
                    ? "Multiple locations (see each day section)"
                    : derivedLocationLabelsHdr[0] ||
                      (filterLocations.length > 0
                        ? filterLocations
                            .map(
                              (id) =>
                                filterLocationList.find((l) => String(l.id) === id)?.name ||
                                `#${id}`,
                            )
                            .join(", ")
                        : "");
                const fallbackSupervisorFromRequestHdr = (() => {
                  const byName = [
                    selectedRequest?.supervisor,
                    selectedRequest?.supervisorName,
                    selectedRequest?.supervisor_name,
                    editableRequest?.supervisor,
                    editableRequest?.supervisorName,
                    editableRequest?.supervisor_name,
                  ]
                    .map((v) => String(v || "").trim())
                    .find(Boolean);
                  if (byName) return byName;

                  const supId = String(
                    editableRequest?.supervisor_contact_account_id ||
                      selectedRequest?.supervisor_contact_account_id ||
                      "",
                  ).trim();
                  if (!supId) return "";

                  const sup = (supervisors || []).find((s) => {
                    const sid = s?.id || s?.account_id || s?.contact_id || s?.contactId;
                    return String(sid || "") === supId;
                  });

                  return (
                    String(
                      sup?.name ||
                        `${sup?.first_name || ""} ${sup?.last_name || ""}`,
                    )
                      .trim() || ""
                  );
                })();
                const supervisorHeaderLabelHdr = (() => {
                  const unique = Array.from(
                    new Set(
                      String(supervisorsListHdr || "")
                        .split(",")
                        .map((v) => v.trim())
                        .filter(Boolean),
                    ),
                  );
                  if (unique.length > 1)
                    return "Multiple supervisors (see each day section)";
                  return unique[0] || fallbackSupervisorFromRequestHdr || "";
                })();

                const timesheetClientAvatarSrc = (() => {
                  const sel = (filterClientList || []).find(
                    (c) => String(c.id) === String(filterClient),
                  );
                  const fromList =
                    sel?.profile_picture && String(sel.profile_picture).trim();
                  if (fromList) return fromList;
                  const fromRow = (displayRows || []).find(
                    (r) =>
                      r?.client_profile_picture &&
                      String(r.client_profile_picture).trim(),
                  );
                  const rowPic = fromRow?.client_profile_picture
                    ? String(fromRow.client_profile_picture).trim()
                    : "";
                  if (rowPic) {
                    if (rowPic.startsWith("http")) return rowPic;
                    if (rowPic.startsWith("/"))
                      return `${String(BASE_URL || "").replace(/\/$/, "")}${rowPic}`;
                    return rowPic;
                  }
                  if (filterClient)
                    return `${BASE_URL}/api/avatars/client/${filterClient}`;
                  return "";
                })();

                const timesheetClientInitialsHdr = (() =>
                  (clientSearch || "CL")
                    .trim()
                    .split(/\s+/)
                    .map((w) => (w[0] || "").toUpperCase())
                    .join("")
                    .substring(0, 2) || "CL")();

                if (loading) {
                  return (
                    <div className="overflow-hidden rounded-lg border mb-6">
                      <div className="px-3 py-4 text-center">Loading…</div>
                    </div>
                  );
                }

                if (filterMode === "week") {
                  const hasFilters =
                    filterStartDate ||
                    filterClient ||
                    filterLocations.length > 0 ||
                    (searchTerm && searchTerm.trim() !== "");

                  const weekDateRange =
                    dateRange.length > 0
                      ? dateRange
                      : displayRows.length > 0
                        ? Array.from(timesheetsByDate.keys()).sort()
                        : [];

                  if (weekDateRange.length === 0) {
                    return (
                      <div className="overflow-hidden rounded-lg border mb-6">
                        <div className="px-3 py-4 text-center text-muted-foreground">
                          {hasFilters
                            ? "No timesheets match the selected filters."
                            : "Please select a filter (date, client, location, or search)."}
                        </div>
                      </div>
                    );
                  }

                  const dataInRange = filterTimesheetsToWeekRange(
                    displayRows,
                    weekDateRange,
                  );
                  let locationGroups = groupTimesheetsByLocation(dataInRange);

                  if (locationGroups.length === 0) {
                    const fallbackLabel =
                      locationLabelHdr &&
                      locationLabelHdr !==
                        "Multiple locations (see each day section)"
                        ? locationLabelHdr
                        : filterLocations.length > 0
                          ? filterLocations
                              .map(
                                (id) =>
                                  filterLocationList.find(
                                    (l) => String(l.id) === id,
                                  )?.name || `#${id}`,
                              )
                              .join(", ")
                          : "Unspecified Location";
                    locationGroups = [
                      {
                        key: "none",
                        label: fallbackLabel,
                        locationId:
                          filterLocations.length === 1 ? filterLocations[0] : "",
                        rows: [],
                      },
                    ];
                  }

                  const endDateIso = endDateIsoHdr;
                  const resolvedClientAvatar =
                    resolveBackendUrl(timesheetClientAvatarSrc) ||
                    timesheetClientAvatarSrc;

                  return (
                    <>
                      {locationGroups.map((locGroup, locIdx) => {
                        const staffRows = buildWeekStaffRows(
                          locGroup.rows,
                          filterStartDate,
                        );
                        const firstRow = locGroup.rows[0];
                        const locPositionTitle = firstRow
                          ? firstRow.position ||
                            firstRow.role ||
                            firstRow.position_name ||
                            positionSummaryHdr
                          : positionSummaryHdr;
                        const locSupervisors =
                          resolveSupervisorsFromRows(locGroup.rows) ||
                          supervisorHeaderLabelHdr;
                        const locStaffRequestIds =
                          resolveStaffRequestIdsFromRows(locGroup.rows) ||
                          staffRequestIdsHdr;

                        return (
                          <TimesheetWeekView
                            key={`week-loc-${locGroup.key}-${locIdx}`}
                            weekDateRange={weekDateRange}
                            staffRows={staffRows}
                            locationLabel={locGroup.label}
                            qrLocationId={locGroup.locationId || ""}
                            clientName={clientSearch || "Client"}
                            clientAvatarSrc={resolvedClientAvatar}
                            clientInitials={timesheetClientInitialsHdr}
                            dateRangeLabel={dateRangeLabel}
                            supervisorsLabel={locSupervisors}
                            staffRequestIds={locStaffRequestIds}
                            positionTitle={locPositionTitle}
                            talentLogoSrc={talentLogo2}
                            filterClient={filterClient}
                            baseDateHdr={baseDateHdr}
                            rangeDaysHdr={rangeDaysHdr}
                            endDateIso={endDateIso}
                            clientSearchName={clientSearch}
                            AvatarSmall={AvatarSmall}
                            printWithoutValues={printWithoutValues}
                          />
                        );
                      })}
                    </>
                  );
                }

                if (finalDateRange.length === 0) {
                  return (
                    <div className="overflow-hidden rounded-lg border mb-6">
                      <div className="px-3 py-8 text-center text-muted-foreground">
                        <div className="text-base font-medium mb-1">No Results Found</div>
                        {!filterClient && (
                          <div className="text-sm text-gray-400">Select a client to view timesheets.</div>
                        )}
                      </div>
                    </div>
                  );
                }

                const resolveTimesheetLocationInfo = (timesheet) => {
                  const locationId = String(
                    timesheet?.client_location_id ||
                      timesheet?.location_id ||
                      timesheet?.setting_client_location_id ||
                      timesheet?.locationId ||
                      timesheet?.clientLocationId ||
                      "",
                  ).trim();

                  const locationName = String(
                    timesheet?.client_location ||
                      timesheet?.clientLocationName ||
                      timesheet?.location_name ||
                      timesheet?.location ||
                      timesheet?.locationName ||
                      timesheet?.full_address ||
                      timesheet?.address ||
                      timesheet?.site ||
                      timesheet?.venue ||
                      "",
                  ).trim();

                  // Use id first so same-named locations still split into separate pages.
                  const locationKey = locationId
                    ? `id:${locationId}`
                    : locationName
                      ? `name:${locationName.toLowerCase()}`
                      : "none";

                  return {
                    key: locationKey,
                    label:
                      locationName ||
                      (locationId ? `Location #${locationId}` : "Unspecified Location"),
                    qrLocationId: locationId,
                  };
                };

                const resolveSupervisorsLabel = (rows) => {
                  const values = (rows || [])
                    .map((r) =>
                      r?.supervisor ||
                      r?.supervisors ||
                      r?.supervisorName ||
                      r?.shiftSupervisor ||
                      r?.supervisor_name ||
                      "",
                    )
                    .filter(Boolean)
                    .flatMap((raw) =>
                      Array.isArray(raw)
                        ? raw
                        : String(raw)
                            .split(",")
                            .map((v) => v.trim())
                            .filter(Boolean),
                    );
                  const unique = Array.from(new Set(values));
                  return unique.join(", ");
                };

                const resolveDefaultSupervisorLabel = () => {
                  const byName = [
                    selectedRequest?.supervisor,
                    selectedRequest?.supervisorName,
                    selectedRequest?.supervisor_name,
                    editableRequest?.supervisor,
                    editableRequest?.supervisorName,
                    editableRequest?.supervisor_name,
                  ]
                    .map((v) => String(v || "").trim())
                    .find(Boolean);
                  if (byName) return byName;

                  const supId = String(
                    editableRequest?.supervisor_contact_account_id ||
                      selectedRequest?.supervisor_contact_account_id ||
                      "",
                  ).trim();
                  if (!supId) return "";

                  const sup = (supervisors || []).find((s) => {
                    const sid = s?.id || s?.account_id || s?.contact_id || s?.contactId;
                    return String(sid || "") === supId;
                  });

                  return (
                    String(
                      sup?.name ||
                        `${sup?.first_name || ""} ${sup?.last_name || ""}`,
                    )
                      .trim() ||
                    ""
                  );
                };

                const dateLocationPages = [];
                const hasAnyTimesheets = Array.from(timesheetsByDate.values()).some(
                  (rows) => (rows || []).length > 0,
                );

                if (!hasAnyTimesheets) {
                  finalDateRange.forEach((dateStr, dateIdx) => {
                    dateLocationPages.push({
                      dateStr,
                      dateIdx,
                      locationLabel: "Unspecified Location",
                      qrLocationId:
                        filterLocations.length === 1 ? String(filterLocations[0]) : "",
                      supervisorsLabel: "",
                      timesheets: [],
                    });
                  });
                } else {
                  const orderedLocations = [];
                  const locationGroups = new Map();

                  finalDateRange.forEach((dateStr) => {
                    const dateTimesheets = timesheetsByDate.get(dateStr) || [];
                    dateTimesheets.forEach((ts) => {
                      const loc = resolveTimesheetLocationInfo(ts);
                      if (!locationGroups.has(loc.key)) {
                        locationGroups.set(loc.key, {
                          key: loc.key,
                          label: loc.label,
                          qrLocationId: loc.qrLocationId,
                          byDate: new Map(),
                        });
                        orderedLocations.push(loc.key);
                      }
                      const group = locationGroups.get(loc.key);
                      if (!group.byDate.has(dateStr)) {
                        group.byDate.set(dateStr, []);
                      }
                      group.byDate.get(dateStr).push(ts);
                    });
                  });

                  orderedLocations.forEach((locationKey) => {
                    const group = locationGroups.get(locationKey);
                    finalDateRange.forEach((dateStr, dateIdx) => {
                      const rows = group?.byDate?.get(dateStr) || [];
                      if (rows.length === 0) return;

                      dateLocationPages.push({
                        dateStr,
                        dateIdx,
                        locationLabel: group.label,
                        qrLocationId: group.qrLocationId,
                        supervisorsLabel: resolveSupervisorsLabel(rows),
                        timesheets: rows,
                      });
                    });
                  });
                }

                // Group pages by location
                const pagesByLocation = new Map();
                dateLocationPages.forEach((page) => {
                  if (!pagesByLocation.has(page.locationLabel)) {
                    pagesByLocation.set(page.locationLabel, []);
                  }
                  pagesByLocation.get(page.locationLabel).push(page);
                });

                return (
                  <>
                    {Array.from(pagesByLocation.entries()).map(
                  ([locationLabel, locationPages], locIdx) => {
                    // Get first page's timesheets for location header
                    const locationTimesheets = locationPages.flatMap((p) => p.timesheets);
                    const firstTimesheet = locationTimesheets[0] || {};
                    const locationId =
                      firstTimesheet.client_location_id ||
                      firstTimesheet.clientLocationId ||
                      firstTimesheet.location_id ||
                      firstTimesheet.locationId ||
                      "";
                    const positionTitle =
                      firstTimesheet.position ||
                      firstTimesheet.role ||
                      firstTimesheet.position_name ||
                      selectedRequest?.job_title ||
                      editableRequest?.job_title ||
                      "Position";
                    
                    // Extract supervisors for this location with better fallback
                    let supervisorsForLocation = "";
                    
                    // First try to get from timesheet rows
                    const tsSupvisors = Array.from(
                      new Set(
                        locationTimesheets
                          .map(
                            (ts) =>
                              ts?.supervisor ||
                              ts?.supervisors ||
                              ts?.supervisorName ||
                              ts?.supervisor_name ||
                              "",
                          )
                          .filter(Boolean),
                      ),
                    ).join(", ");
                    
                    if (tsSupvisors) {
                      supervisorsForLocation = tsSupvisors;
                    } else {
                      // Fallback: get from request details
                      const fromRequest = [
                        selectedRequest?.supervisor,
                        selectedRequest?.supervisorName,
                        selectedRequest?.supervisor_name,
                        editableRequest?.supervisor,
                        editableRequest?.supervisorName,
                        editableRequest?.supervisor_name,
                      ]
                        .map((v) => String(v || "").trim())
                        .find(Boolean);
                      
                      if (fromRequest) {
                        supervisorsForLocation = fromRequest;
                      } else {
                        // Try ID lookup
                        const supId = String(
                          editableRequest?.supervisor_contact_account_id ||
                            selectedRequest?.supervisor_contact_account_id ||
                            "",
                        ).trim();
                        if (supId) {
                          const sup = (supervisors || []).find((s) => {
                            const sid = s?.id || s?.account_id || s?.contact_id || s?.contactId;
                            return String(sid || "") === supId;
                          });
                          if (sup) {
                            supervisorsForLocation =
                              sup?.name || `${sup?.first_name || ""} ${sup?.last_name || ""}`;
                          }
                        }
                      }
                    }
                    
                    const staffRequestIdsForLocation = Array.from(
                      new Set(
                        locationTimesheets
                          .map((ts) => ts?.staff_request_id || "")
                          .filter(Boolean),
                      ),
                    ).join(", ");

                    const buildCheckinUrl = (dateStr, locationLabel, locationId) => {
                      const params = new URLSearchParams();
                      if (filterClient) params.set("clientId", String(filterClient));
                      if (dateStr) {
                        params.set("shiftDate", dateStr);
                        params.set("from", dateStr);
                        params.set("to", dateStr);
                      }
                      if (clientSearch) params.set("client", clientSearch);
                      if (locationLabel) params.set("location", locationLabel);
                      if (locationId) params.set("locationId", String(locationId));
                      params.set("forceLogin", "1");
                      const origin =
                        typeof window !== "undefined" ? window.location.origin : "";
                      return `${origin}/checkin?${params.toString()}`;
                    };

                    return (
                      <div key={`location-group-${locIdx}`} className="print-location-group mb-8 last:mb-0">
                        {/* Location header — once per location (legacy app-heading) */}
                        <div className="print-timesheet-location-header flex items-center justify-between gap-4 border-b border-gray-200">
                          <div className="flex items-center gap-3 min-w-0 flex-1">
                            <img
                              src={talentLogo2}
                              alt="Talent Logo"
                              className="h-10 w-auto flex-shrink-0"
                            />
                            <div
                              className={`print-client-avatar flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full ${getAvatarColorClass(clientSearch)} text-white font-semibold text-sm overflow-hidden`}
                            >
                              {timesheetClientAvatarSrc ? (
                                <img
                                  src={timesheetClientAvatarSrc}
                                  alt=""
                                  className="h-full w-full object-cover"
                                />
                              ) : (
                                timesheetClientInitialsHdr
                              )}
                            </div>
                            <div className="min-w-0 print-client-meta">
                              <div className="print-client-name font-bold text-gray-900">
                                {clientSearch || "Client"}
                              </div>
                              {locationLabel && (
                                <div className="text-gray-700 text-sm uppercase">
                                  {locationLabel}
                                </div>
                              )}
                              <div className="text-gray-600 text-xs">
                                Supervisor(s):{" "}
                                <span className="font-medium">
                                  {supervisorsForLocation || "\u2014"}
                                </span>
                              </div>
                              {staffRequestIdsForLocation ? (
                                <div className="text-gray-600 text-xs">
                                  Staff Request:{" "}
                                  <span className="font-medium">
                                    {staffRequestIdsForLocation}
                                  </span>
                                </div>
                              ) : null}
                            </div>
                          </div>
                          <a
                            href={buildCheckinUrl(
                              locationPages[0]?.dateStr,
                              locationLabel,
                              locationPages[0]?.qrLocationId,
                            )}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="print-header-qr flex-shrink-0 inline-flex items-center justify-center p-1 bg-white border border-gray-200 rounded"
                            aria-label="QR Check-in"
                          >
                            <QRCodeSVG
                              value={buildCheckinUrl(
                                locationPages[0]?.dateStr,
                                locationLabel,
                                locationPages[0]?.qrLocationId,
                              )}
                              size={72}
                              level="M"
                            />
                          </a>
                        </div>

                        {/* Date pages for this location */}
                        {locationPages.map((page, pageIdx) => {
                          const slots = groupTimesheetsIntoSlots(page.timesheets);
                          const dateAnchor = parseDbDateTime(`${page.dateStr}T12:00:00Z`);
                          const weekday = formatAppIntlLabel(dateAnchor, { weekday: "long" });
                          const monthDay = formatAppIntlLabel(dateAnchor, {
                            month: "long",
                            day: "numeric",
                          });
                          const totalScheduled = page.timesheets
                            .reduce(
                              (sum, r) => sum + (parseFloat(r.duration) || 0),
                              0,
                            )
                            .toFixed(1);

                          // Calculate dynamic column widths based on longest position title
                          const maxPosLen = (page.timesheets || []).reduce((max, r) => {
                            const s = String(r.displayRole || r.role || r.position || "");
                            return Math.max(max, s.trim().length);
                          }, 0);

                          let posWidth = 16;
                          if (maxPosLen > 25) {
                            posWidth = 26;
                          } else if (maxPosLen > 18) {
                            posWidth = 22;
                          } else if (maxPosLen > 13) {
                            posWidth = 19;
                          } else {
                            posWidth = 16;
                          }
                          const nameWidth = 48 - posWidth;

                          return (
                            <div
                              key={`date-location-${page.dateStr}-${page.locationLabel}-${pageIdx}`}
                              className="print-date-page overflow-hidden mb-4 last:mb-0"
                            >
                              {pageIdx > 0 ? (
                                <div className="print-timesheet-qr-row">
                                  <a
                                    href={buildCheckinUrl(
                                      page.dateStr,
                                      page.locationLabel,
                                      page.qrLocationId,
                                    )}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center justify-center p-1 bg-white border border-gray-200 rounded"
                                    aria-label="QR Check-in"
                                  >
                                    <QRCodeSVG
                                      value={buildCheckinUrl(
                                        page.dateStr,
                                        page.locationLabel,
                                        page.qrLocationId,
                                      )}
                                      size={72}
                                      level="M"
                                    />
                                  </a>
                                </div>
                              ) : null}

                              {/* Compact day bar — date + total only (legacy day-heading) */}
                              <div className="print-timesheet-day-bar bg-[#DC1F00] text-white">
                                <div>
                                  <span className="print-day-weekday">{weekday}</span>
                                  {" "}
                                  <span className="print-day-date">{monthDay}</span>
                                </div>
                                <span className="print-day-total">
                                  {totalScheduled}h total scheduled
                                </span>
                              </div>

                      <div className="print-date-page-main">
                      <table
                        className="print-timesheet-table w-full border-collapse"
                        style={{
                          "--pos-col-width": `${posWidth}%`,
                          "--name-col-width": `${nameWidth}%`,
                        }}
                      >
                        <colgroup>
                          <col style={{ width: `${posWidth}%` }} />
                          <col style={{ width: `${nameWidth}%` }} />
                          <col style={{ width: "9.5%" }} />
                          <col style={{ width: "8.5%" }} />
                          <col style={{ width: "10.5%" }} />
                          <col style={{ width: "12%" }} />
                          <col style={{ width: "11.5%" }} />
                        </colgroup>
                        <tbody>
                          {slots.length === 0 ? (
                            <tr>
                              <td
                                colSpan={7}
                                className="px-3 py-4 text-center text-muted-foreground"
                              >
                                No timesheets for this date.
                              </td>
                            </tr>
                          ) : (
                            slots.map((slot, si) => {
                              const timeLabel =
                                slot.time ||
                                `${formatTimeRange(slot.start_time, slot.end_time)}`;
                              const durationLabel =
                                typeof slot.hours !== "undefined"
                                  ? `${Number(slot.hours).toFixed(1).replace(/\.0$/, "")}h`
                                  : slot.duration || "";

                              const positions = slot.positions || [];

                              return (
                                <React.Fragment key={`slot-${pageIdx}-${si}`}>
                                  <tr className="print-timesheet-slot-header bg-gray-200 text-xs text-gray-600">
                                    <td className="border font-normal">
                                      {timeLabel}
                                    </td>
                                    <td className="border font-normal">
                                      {durationLabel}
                                    </td>
                                    <td className="border font-normal">
                                      Check-in
                                    </td>
                                    <td className="border font-normal">
                                      Break (min)
                                    </td>
                                    <td className="border font-normal">
                                      Check-out
                                    </td>
                                    <td className="border font-normal">
                                      Total
                                    </td>
                                    <td className="border font-normal">
                                      Signature
                                    </td>
                                  </tr>
                                  {positions.map((p, pi) => {
                                    const printCheckin = printWithoutValues ? "" : (formatTime(p.checkin_time) || "");
                                    const printCheckout = printWithoutValues ? "" : (formatTime(p.checkout_time) || "");
                                    const bm = p.break_minutes;
                                    const printBreak = printWithoutValues 
                                      ? "" 
                                      : (bm != null && bm !== "" && !Number.isNaN(Number(bm))
                                        ? String(Math.round(Number(bm)))
                                        : "");
                                    let printTotal = "";
                                    if (!printWithoutValues) {
                                      if (
                                        p.total_after_break != null &&
                                        p.total_after_break !== ""
                                      ) {
                                        const n =
                                          typeof p.total_after_break === "number"
                                            ? p.total_after_break
                                            : parseFloat(
                                                String(p.total_after_break).replace(
                                                  /[^0-9.-]/g,
                                                  "",
                                                ),
                                              );
                                        if (!Number.isNaN(n) && n >= 0) {
                                          printTotal = formatDuration(n);
                                        }
                                      }
                                    }
                                    const printCellCls = (text) =>
                                      `border text-sm text-center${
                                        !text || !String(text).trim()
                                          ? " print-empty-field"
                                          : ""
                                      }`;

                                    return (
                                    <tr
                                      key={`pos-${pageIdx}-${si}-${pi}`}
                                      className="print-timesheet-data-row border-t"
                                    >
                                      <td className="border text-foreground font-normal">
                                        <span className="block truncate font-normal print-position-title" title={p.displayRole || p.role}>
                                          {p.displayRole || p.role}
                                        </span>
                                      </td>
                                      <td className="border text-foreground font-normal">
                                        <div className="flex items-center">
                                        {(
                                          p.temp ||
                                          p.staff ||
                                          ""
                                        ).toLowerCase() !== "unassigned" &&
                                        (p.temp || p.staff) ? (
                                          <>
                                            <AvatarSmall
                                              name={p.temp || p.staff}
                                              src={
                                                p.avatar_url ||
                                                p.profile_picture
                                              }
                                            />
                                            <div className="flex flex-col min-w-0">
                                            <span className="text-foreground flex items-center gap-1 print-staff-name font-normal">
                                              {p.temp || p.staff}
                                              {shiftRowHasTravel(
                                                p.travel_time_amount,
                                                p.travel_time_type,
                                              ) && (
                                                <span
                                                  className="inline-flex items-center"
                                                  title={travelPayRateSuffix(
                                                    p.travel_time_type,
                                                    p.travel_time_amount,
                                                  )}
                                                >
                                                  <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    viewBox="0 0 16 16"
                                                    className="w-4 h-4 text-yellow-600"
                                                    aria-hidden="true"
                                                  >
                                                    <g transform="rotate(45 8 8)">
                                                      <rect
                                                        x="2"
                                                        y="2"
                                                        width="12"
                                                        height="12"
                                                        rx="1"
                                                        fill="#b5851d"
                                                      />
                                                      <path
                                                        d="M6 8h4M8 6l2 2-2 2"
                                                        fill="none"
                                                        stroke="#ffffff"
                                                        strokeWidth="1.8"
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                      />
                                                    </g>
                                                  </svg>
                                                </span>
                                              )}
                                            </span>
                                            {p.carpool_name && (
                                              <span className="flex items-start gap-1">
                                                <FaCar
                                                  className="text-red-600 shrink-0 mt-0.5"
                                                  size={10}
                                                />
                                                <span className="min-w-0 flex-1 text-left text-xs text-red-600 [overflow-wrap:anywhere] break-all whitespace-normal">
                                                  {p.carpool_name}
                                                </span>
                                              </span>
                                            )}
                                            </div>
                                          </>
                                        ) : (
                                          <>
                                            <PlaceholderAvatar />
                                            <span className="text-muted-foreground print-staff-name font-normal">
                                              Unassigned
                                            </span>
                                          </>
                                        )}
                                        </div>
                                      </td>
                                      <td className={printCellCls(printCheckin)}>
                                        {printCheckin || "\u00a0"}
                                      </td>
                                      <td className={printCellCls(printBreak)}>
                                        {printBreak || "\u00a0"}
                                      </td>
                                      <td className={printCellCls(printCheckout)}>
                                        {printCheckout || "\u00a0"}
                                      </td>
                                      <td className={printCellCls(printTotal)}>
                                        {printTotal || "\u00a0"}
                                      </td>
                                      <td className="border print-empty-field"><>&nbsp;</></td>
                                    </tr>
                                    );
                                  })}
                                </React.Fragment>
                              );
                            })
                          )}
                        </tbody>
                      </table>

                      {/* Signature block — legacy horizontal layout */}
                      <div className="print-timesheet-signature">
                        <span>Client Signature</span>
                        <span className="print-signature-line" />
                        <span>Client Printed Name</span>
                        <span className="print-signature-line" />
                        <span>Total Hours</span>
                        <span className="print-signature-line" />
                      </div>
                      </div>
                    </div>
                          );
                        })}
                      </div>
                    );
                  },
                )}
                  </>
                );
              })()}
            </div>
          </div>
    </div>
  );
}
